import { DeleteOutlined, FireOutlined, LockOutlined } from "@ant-design/icons";
import { Button, message, Modal, Switch, Typography } from "antd";
import { useState } from "react";
import { useAuth } from "../accounts/AuthContext";
import { requestPasswordReset } from "../api/allauth";
import api from "../api/axios.js";
import { useSettings } from "../contexts/SettingsContext";
import { useUX } from "../contexts/UXContext";
import { ROUTES } from "../routes.js";

import styles from "./Settings.module.css";

const { Text, Paragraph } = Typography;


const Settings = () => {
    const { open, closeSettings } = useSettings();
    const { auth } = useAuth();
    const [passwordResetSent, setPasswordResetSent] = useState(false);
    const { loading, isBeta, toggleBeta } = useUX();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handlePasswordReset = async () => {
        const email = auth?.data?.user?.email;

        if (!email) {
            message.error("Your account does not have an email address. Cannot send reset link.");
            return;
        }

        try {
            setPasswordResetSent(true);

            const response = await requestPasswordReset(email);

            if (response?.status && response.status !== 200) {
                const err = response.errors?.[0];

                if (err?.code === "invalid") {
                    message.error("Your email address is invalid.");
                } else if (err?.code === "email_not_found") {
                    message.error("No account is registered with this email.");
                } else {
                    message.error(err?.message || "Could not process reset request.");
                }

                setPasswordResetSent(false);  // allow retry
                return;
            }

            message.success("Password reset email sent! Check your inbox.");
        } catch (err) {
            console.error("Password reset network/server error:", err);

            if (err?.response?.status >= 500) {
                message.error("The server is currently unavailable. Try again later.");
            } else {
                message.error("Network error. Please try again later. If issue persists, please contact support.");
            }

            setPasswordResetSent(false);  // allow retry
        }
    };

    const handleDeleteAccount = async () => {
        setDeleting(true);

        try {
            await api.post("/accounts/delete-account/", null);

            message.success("Your account has been deleted. We're sorry to see you go.");
            setDeleteModalOpen(false);

            window.location.href = ROUTES.ACCOUNT.SIGNUP;
        } catch (err) {
            console.error("Account deletion error:", err);
            message.error("Failed to delete account. Please try again later and contact support if issue persists.");
            setDeleting(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                centered
                onCancel={closeSettings}
                footer={null}
                width={420}
                className={styles["settings-modal"]}
            >
                <h2 className={styles["settings-title"]}>Account Settings</h2>

                <div className={styles["settings-section"]}>
                    <div className={styles["settings-row"]}>
                        <LockOutlined className={styles["settings-icon"]} />
                        <div className={styles["settings-label"]}>Reset Password</div>
                        <Button size="small" disabled={passwordResetSent} onClick={handlePasswordReset}>
                            Send Email
                        </Button>
                    </div>

                    <div className={styles["settings-row"]}>
                        <FireOutlined className={styles["settings-icon"]} />
                        <div className={styles["settings-label"]}>Join Beta Program</div>
                        <Switch
                            className={styles["settings-toggle"]}
                            checked={isBeta}
                            onChange={(checked) => toggleBeta()}
                            disabled={loading}
                            style={{
                                backgroundColor: isBeta ? "var(--brand)" : undefined,
                            }}
                        />
                    </div>


                    <div className={styles["settings-danger-section"]}>
                        <h3 className={styles["settings-danger-title"]}>Danger Zone</h3>
                        <div className={styles["settings-row"]}>
                            <DeleteOutlined className={styles["settings-icon"]} />
                            <div className={styles["settings-label"]}>Delete Account</div>
                            <Button
                                danger
                                type="default"
                                className={styles["settings-danger-btn"]}
                                size="small"
                                onClick={() => setDeleteModalOpen(true)}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Delete account Confirmation Modal */}
            <Modal
                open={deleteModalOpen}
                centered
                onCancel={() => setDeleteModalOpen(false)}
                className={styles["confirm-modal"]}
                footer={[
                    <Button 
                    key="cancel" 
                    className={styles["cancel-btn"]}
                    onClick={() => setDeleteModalOpen(false)}
                    >
                        No, keep my account
                    </Button>,
                    <Button
                        key="confirm"
                        danger
                        type="primary"
                        className={styles["confirm-danger-btn"]}
                        loading={deleting}
                        onClick={handleDeleteAccount}
                    >
                        Yes, do it
                    </Button>,
                ]}
                title="Confirm Account Deletion"
            >
                <Paragraph>
                    By deleting your account, your personal information will be removed and you’ll lose access to your account.
                </Paragraph>
                <Paragraph>
                    This action is <Text strong type="danger">irreversible</Text>.
                </Paragraph>
                <Paragraph>Do you want to proceed?</Paragraph>
            </Modal>
        </>
    );
}

export default Settings;